import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { FilterEditor } from '@adornis/filter/client/filter-editor.js';
import { subscribePaginatedAndFiltered } from '@adornis/filter/db/subscribePaginatedAndFiltered.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { buildChancenDataViewOptions } from '@chancenwerk/client/design/components/cr-data-view.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { TeamMeeting } from '@chancenwerk/shared/attendance/entities/TeamMeeting.js';
import '@chancenwerk/client/components/chancen-component-context-menu.js';
import '@chancenwerk/client/components/semantics/chancen-component-semantic-kpf-picker.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import '@chancenwerk/client/design/components/cr-progress-bar.js';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import '../components/board-component-course-digital-card.js';
import { KPFAPI } from '@chancenwerk/shared/master-data-management/kpf/api/KPFAPI.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';

/**
 * @customElement board-page-team-treffen-overview
 */
@customElement('board-page-team-treffen-overview')
export class BoardPageTeamTreffenOverview extends CrPage {
  constructor() {
    super();
    this.label = 'Team Treffen';
    this.description = 'Hier kannst du Team Treffen erstellen und deren Anwesenheiten verwalten';
    this.permission = ChancenPermission.board.EDIT_TEAM_TREFFEN;
  }

  override header() {
    return html` <chancen-component-context-menu
      .items=${[
        {
          icon: 'add',
          label: 'Hinzufügen',
          action: async () => {
            ChromiumDialog.showPopup({
              content: resolve => {
                let date = DateTime.now();
                let from = DateTime.now().set({ minute: 0 });
                let until = DateTime.now().set({ minute: 30 });
                let kpfId = '';

                return html`<cr-flex padding="lg" space="md">
                  <cr-h3>Neues Team Treffen</cr-h3>
                  <cr-hr> </cr-hr>
                  <x-entity-picker
                    flex
                    placeholder="KPF"
                    clearable
                    .options=${{
                      class: KPF,
                      operation: KPFAPI.subscribeByFilter,
                      selectionSet: {
                        name: 1,
                        kuerzel: 1,
                        produkt: { _class: 1, kuerzel: 1 },
                        school: { name: 1, kuerzel: 1 },
                      },
                      filter: new AdornisFilter({}),
                    }}
                    @value-picked=${(e: ValueEvent<Maybe<string>>) => {
                      kpfId = e.detail.value ?? '';
                    }}
                  ></x-entity-picker>
                  <cr-date-picker-dropdown
                    .end-year=${DateTime.now().plus({ years: 10 }).year}
                    .value=${date}
                    placeholder="Datum"
                    click-to-save
                    flex
                    @value-picked=${(e: ValueEvent<DateTime>) => {
                      date = e.detail.value;
                    }}
                  ></cr-date-picker-dropdown>
                  <cr-flex horizontal crossaxis-center space="md">
                    <cr-input
                      flex
                      type="time"
                      .value=${from}
                      @value-changed=${(e: ValueEvent<DateTime>) => {
                        from = e.detail.value;
                      }}
                    ></cr-input>
                    <cr-text> bis </cr-text>
                    <cr-input
                      flex
                      type="time"
                      .value=${until}
                      @value-changed=${(e: ValueEvent<DateTime>) => {
                        until = e.detail.value;
                      }}
                    ></cr-input>
                  </cr-flex>
                  <cr-button
                    @click=${async () => {
                      const id = await new TeamMeeting({
                        createdBy: this._currentUser.value?._id,
                        when: date.set({ hour: from.hour, minute: from.minute }),
                        until: date.set({ hour: until.hour, minute: until.minute }),
                        kpfId,
                      }).create();
                      goTo(`/team-treffen/${id}`);
                    }}
                  >
                    Erstellen
                  </cr-button>
                </cr-flex>`;
              },
            });
          },
        },
      ]}
    >
      Inhalt Hinzufügen
    </chancen-component-context-menu>`;
  }

  override content() {
    const options = buildChancenDataViewOptions<typeof TeamMeeting>(
      {
        class: TeamMeeting,
        operation: (skip, limit, filter) => {
          const finalFilter = new AdornisFilter({
            aspects: [
              new StringAspect({ mode: 'equals', fieldName: 'createdBy', value: this._currentUser.value?._id }),
              ...filter.aspects,
            ],
          });
          return subscribePaginatedAndFiltered(TeamMeeting, skip, limit, finalFilter) as any;
        },
        view: new View({ filter: new AdornisFilter({ aspects: [] }) }),
        rowHref: e => {
          return `/team-treffen/${e._id}`;
        },
        customColumns: [
          {
            fieldName: 'when',
            renderHeader: () => {
              return html`<cr-flex w100 crossaxis-center space="sm" padding="sm" ${css({ justifyContent: 'center' })}>
                <cr-text bold>Team Treffen</cr-text>
              </cr-flex>`;
            },
            selectionSet: {
              when: 1,
              until: 1,
              kpfId: 1,
              kpf: { kuerzel: 1, produkt: { kuerzel: 1, name: 1 }, school: { kuerzel: 1, name: 1 } },
            },
            getFieldMapping: field =>
              html`<cr-flex crossaxis-center space="sm" padding="sm">
                <cr-icon ${css({ fontSize: '1.2rem' })}>event</cr-icon>
                <cr-text bold>${field.when?.toFormat('dd.MM.yyyy')} ${field.until?.toFormat('HH:mm')}</cr-text>
                <cr-text>${field.kpf?.school?.name} ${field.kpf?.kuerzel}</cr-text>
              </cr-flex>`,
          },
        ],
      } as DataViewOptions<typeof TeamMeeting>,
      { fieldAspectGeneration: { hideFieldsWithoutTranslation: true, whitelist: { when: 1 } } },
    );

    return html`<cr-flex padding="md" space="md">
      <cr-card padding="sm"> ${FilterEditor({ options: options.filterOptions })} </cr-card>
      <cr-data-view .options=${options.dataViewOptions}> </cr-data-view>
    </cr-flex>`;
  }
}
